import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { CalendarDays, LayoutDashboard, MoreHorizontal, Music4, X } from 'lucide-react'
import { navItemsFor } from '@/components/layout/nav'
import { usePermissions } from '@/hooks/usePermissions'
import { cn } from '@/lib/utils'

/**
 * Mobile tab bar — the only navigation below `lg`.
 *
 * THREE destinations, and More appears only if there is something to put in
 * it. Ian, 2026-10-20: "get rid of the redundant double menus underneath".
 * There were four tabs plus a More button that, for most people, opened a
 * sheet holding one entry they were not allowed to use anyway — a second
 * navigation system earning nothing. Volunteers, Teams and Admin are
 * `desktopOnly` now (see nav.ts), so on a phone the overflow is usually
 * empty and the button is simply not drawn.
 *
 * Notifications are deliberately absent: the bell in the header opens the
 * notifications screen, and the header is on screen at every width, so a
 * tab-bar slot would be a second way to the same place.
 *
 * "Schedule" was this tab's label until 2026-09-29. Home became "My Schedule"
 * that day, and two tabs called schedule — one of them yours, one of them
 * everybody's — is the kind of thing you only have to misread once. This tab
 * has always shown who is serving across the church, so it is named for that.
 * The matrix is NOT here; it is on Plans.
 */
const TABS = [
  { to: '/', label: 'Home', icon: LayoutDashboard, end: true },
  { to: '/plans', label: 'Plans', icon: CalendarDays },
  { to: '/songs', label: 'Songs', icon: Music4 },
] as const

const TAB_PATHS = new Set<string>(TABS.map((tab) => tab.to))

/**
 * The pill that slides to the selected tab.
 *
 * Ported from the transitions.dev "tabs sliding" recipe: the pill's width and
 * transform come from the selected cell's own offsetLeft/offsetWidth, so this
 * never needs to know how many tabs there are.
 *
 * Unlike Hub — where a tap loads a whole new document and the pill can only
 * slide on the way out — this is a single page, so the pill genuinely travels
 * from the old route's tab to the new one.
 *
 * `instant` suspends the transition, writes the position, forces a reflow and
 * restores it. That is the recipe's own instruction, and it is what stops the
 * pill sweeping in from zero width on first paint and after a rotation.
 */
function usePill(dependency: unknown) {
  const bar = useRef<HTMLUListElement>(null)
  const pill = useRef<HTMLSpanElement>(null)
  const placed = useRef(false)

  const move = useCallback((instant: boolean) => {
    const cell = bar.current?.querySelector<HTMLElement>('[data-tab].is-tab-selected')
    const node = pill.current
    if (!cell || !node) return

    // Zero: the floating capsule has 8px of padding of its own, and the pill
    // should fill the cell inside it.
    const INSET = 0
    if (instant) node.style.transition = 'none'
    node.style.width = `${Math.max(0, cell.offsetWidth - INSET * 2)}px`
    node.style.transform = `translateX(${cell.offsetLeft + INSET}px)`
    if (instant) {
      void node.offsetWidth // force the reflow before restoring
      node.style.transition = ''
    }
  }, [])

  // Layout, not effect: this has to land before the browser paints.
  useLayoutEffect(() => {
    move(!placed.current)
    placed.current = true
  }, [move, dependency])

  useLayoutEffect(() => {
    let timer: number | undefined
    const onResize = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => move(true), 120)
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('resize', onResize)
    }
  }, [move])

  return { bar, pill }
}

export function BottomNav() {
  const [moreOpen, setMoreOpen] = useState(false)
  const { pathname } = useLocation()
  // Re-measure whenever the selection could have changed: a new route, or the
  // More sheet opening, which puts the pill on the More cell.
  const { bar, pill } = usePill(`${pathname}|${moreOpen}`)

  // Whatever the tab bar has no room for — on a phone, usually nothing.
  const { role } = usePermissions()
  const overflow = navItemsFor(role, { phone: true }).filter((item) => !TAB_PATHS.has(item.to))
  const hasMore = overflow.length > 0

  return (
    <>
      {moreOpen && hasMore ? (
        <div className="fixed inset-0 z-40 lg:hidden no-print" role="dialog" aria-modal="true" aria-label="More">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMoreOpen(false)}
            aria-label="Close"
          />
          <div
            /*
             * Capped and scrollable. It FITS today — six nav entries, four of
             * them tabs, so this holds two and comes out 178px tall on a
             * 375x812 phone. The cap is here because Hub's equivalent sheet
             * had no max-height either, grew to thirteen entries, and ended
             * up 845px tall with its first items off the top of the screen
             * and nothing to scroll. Adding a nav item here should not be
             * able to do that.
             *
             * `dvh` after `vh`: iOS measures `vh` against the chrome-hidden
             * height, so a `vh` cap over-estimates the visible area.
             */
            className="absolute inset-x-0 bottom-[calc(6.25rem+env(safe-area-inset-bottom))] mx-2.5 overflow-y-auto overscroll-contain rounded-2xl border border-border bg-card shadow-2xl animate-slide-down [max-height:calc(100vh-9rem-env(safe-area-inset-bottom))] [max-height:calc(100dvh-9rem-env(safe-area-inset-bottom))]"
            style={{ animationDirection: 'reverse' }}
          >
            <div className="flex items-center justify-between px-4 pb-1 pt-3">
              <span className="section-label">More</span>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <nav aria-label="More destinations" className="px-2 pb-3">
              {overflow.map(({ to, label, icon: Icon, description }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-start gap-3 rounded-lg px-3 py-3 text-sm transition-colors',
                      isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
                    )
                  }
                >
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="min-w-0">
                    <span className="block font-medium">{label}</span>
                    <span className="block text-xs text-muted-foreground">{description}</span>
                  </span>
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      ) : null}

      {/*
        A floating glass capsule, mirroring Hub. The glass itself lives in
        .tabbar in index.css: frosted, never tinted, and built from theme
        tokens so it follows the device between light and dark.
      */}
      <nav aria-label="Primary" className="tabbar lg:hidden no-print">
        <ul ref={bar} className="tabbar-row">
          {/* Behind the labels; width and transform are written by usePill. */}
          <span ref={pill} aria-hidden className="tabbar-pill" />
          {TABS.map(({ to, label, icon: Icon, ...rest }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={'end' in rest ? rest.end : undefined}
                data-tab
                className={({ isActive }) =>
                  cn(
                    'tabbar-item',
                    // The pill reads this class rather than a route match of its
                    // own, so it can never disagree with react-router about
                    // which tab is current — `end` on '/' included.
                    isActive && !moreOpen && 'is-tab-selected is-on',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className={cn('h-5 w-5', isActive && 'stroke-[2.4]')} aria-hidden />
                    {label}
                  </>
                )}
              </NavLink>
            </li>
          ))}

          {/* Only when it would hold something. An empty sheet behind a
              permanent button is the redundant second menu. */}
          {hasMore ? (
            <li>
              <button
                type="button"
                onClick={() => setMoreOpen(true)}
                data-tab
                className={cn('tabbar-item w-full', moreOpen && 'is-tab-selected is-on')}
                aria-expanded={moreOpen}
              >
                <MoreHorizontal className="h-5 w-5" aria-hidden />
                More
              </button>
            </li>
          ) : null}
        </ul>
      </nav>
    </>
  )
}
